import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
	fetchEssayVersions,
	fetchVersionChatMessages,
	markFeedbackVersionSeen,
	patchEssayVersion,
	patchVersionChatReadForCounselor,
	postVersionChatMessage,
} from 'api/essays'
import { syncFeedbackThreadSeen } from 'shared/utils/counselorSeenSync'
import Button from 'shared/ui/Button/Button'
import FeedbackPanel from '../FeedbackPanel/FeedbackPanel'
import PriorityDrawerShell from '../PriorityDrawerShell/PriorityDrawerShell'
import EssayContent from '../EssayContent/EssayContent'
import {
	areFeedbackItemsEqual,
	mapMessagesToFeedbackItems,
	parseJson,
} from '../../utils/feedbackMapping'
import s from './FeedbackReviewDrawer.module.scss'
import { ReactComponent as PenIcon } from 'assets/icons/pen.svg'
import { ReactComponent as DockIcon } from 'assets/icons/dock.svg'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'

const FEEDBACK_POLL_MS = 15000

const FeedbackReviewDrawer = ({
	items,
	index,
	onClose,
	onNavigate,
	onChanged,
	onResolvePending,
}) => {
	const item = items[index]
	const [version, setVersion] = useState(null)
	const [loading, setLoading] = useState(false)
	const [actionLoading, setActionLoading] = useState(false)
	const [feedbackSending, setFeedbackSending] = useState(false)
	const [essayOpen, setEssayOpen] = useState(false)
	const [isEditing, setIsEditing] = useState(false)
	const [titleDraft, setTitleDraft] = useState('')
	const [saving, setSaving] = useState(false)
	const [feedbackItems, setFeedbackItems] = useState([])
	const [feedbackLoading, setFeedbackLoading] = useState(true)
	const [activeFeedbackId, setActiveFeedbackId] = useState(null)
	const editorRef = useRef(null)
	const lastSavedRef = useRef('')
	const itemRef = useRef(item)
	itemRef.current = item

	const markFeedbackSeen = useCallback(() => {
		const current = itemRef.current
		if (!current?.versionId) return
		const anchorAt =
			current?.lastMessageAt || current?.previewMessage?.createdAt || undefined
		markFeedbackVersionSeen(current.versionId, { anchorAt }).catch(() => {})
	}, [])

	const loadFeedback = useCallback(
		async (nextVersion, { silent = false } = {}) => {
			const current = itemRef.current
			if (!current?.essayId || !current?.versionId) return
			if (!silent) setFeedbackLoading(true)
			try {
				const res = await fetchVersionChatMessages(
					current.essayId,
					current.versionId,
				)
				const messages = Array.isArray(res?.data?.messages)
					? res.data.messages
					: []
				const unread = messages.filter(
					m => m?.sender_type === 'student' && !m?.is_read_by_counselor,
				).length
				const hideMessageNew = !current?.isNew
				const nextItems = mapMessagesToFeedbackItems(
					messages,
					nextVersion?.content_text || '',
				).map(message => ({
					...message,
					isUnread: hideMessageNew ? false : message.isUnread,
				}))
				setFeedbackItems(prev =>
					areFeedbackItemsEqual(prev, nextItems) ? prev : nextItems,
				)
				const previewId = current?.previewMessage?.id
				if (previewId && !silent) {
					setActiveFeedbackId(previewId)
				}
				if (!silent) {
					await patchVersionChatReadForCounselor(
						current.essayId,
						current.versionId,
					).catch(() => {})
					syncFeedbackThreadSeen({
						versionId: current.versionId,
						essayId: current.essayId,
						unreadMessageCount: unread,
					})
				}
			} catch (e) {
				console.error(e)
				if (!silent) setFeedbackItems([])
			} finally {
				if (!silent) setFeedbackLoading(false)
			}
		},
		[],
	)

	const loadVersion = useCallback(async () => {
		const current = itemRef.current
		if (!current?.essayId || !current?.versionId) return
		setLoading(true)
		setIsEditing(false)
		setEssayOpen(false)
		setFeedbackLoading(true)
		setFeedbackItems([])
		setActiveFeedbackId(null)
		try {
			const res = await fetchEssayVersions(current.essayId, {
				includeDrafts: true,
			})
			const versions = Array.isArray(res?.data?.versions)
				? res.data.versions
				: []
			const next =
				versions.find(v => String(v.id) === String(current.versionId)) ||
				versions[0] ||
				null
			setVersion(next)
			const title = current.title || next?.title || 'Essay'
			setTitleDraft(title)
			lastSavedRef.current = JSON.stringify({
				title,
				contentJson: parseJson(next?.content_json),
				contentText: next?.content_text || '',
			})
			markFeedbackSeen()
			await loadFeedback(next)
		} catch (e) {
			console.error(e)
			setVersion(null)
			setFeedbackItems([])
		} finally {
			setLoading(false)
		}
	}, [loadFeedback, markFeedbackSeen])

	useEffect(() => {
		if (!item?.essayId || !item?.versionId) return undefined
		loadVersion()
		return undefined
	}, [item?.essayId, item?.versionId, loadVersion])

	useEffect(() => {
		if (!item?.essayId || !item?.versionId) return undefined
		const id = window.setInterval(() => {
			loadFeedback(version, { silent: true })
		}, FEEDBACK_POLL_MS)
		return () => window.clearInterval(id)
	}, [item?.essayId, item?.versionId, loadFeedback, version])

	useEffect(() => {
		if (!essayOpen) return undefined
		const editor = editorRef.current
		if (!editor) return undefined
		editor.clearActiveAnchor?.()
		editor.setAnchors?.([])
		const anchors = feedbackItems
			.map(feedback => {
				if (
					feedback?.resolvedAnchor?.start != null &&
					feedback?.resolvedAnchor?.end != null
				) {
					return {
						id: feedback.id,
						from: feedback.resolvedAnchor.start,
						to: feedback.resolvedAnchor.end,
					}
				}
				return null
			})
			.filter(Boolean)
		if (!anchors.length) return undefined
		let attempts = 0
		const applyAnchors = () => {
			if (!editorRef.current?.isReady?.()) {
				if (attempts < 10) {
					attempts += 1
					window.setTimeout(applyAnchors, 50)
				}
				return
			}
			editorRef.current.setAnchors?.(anchors)
		}
		applyAnchors()
		return undefined
	}, [feedbackItems, item?.versionId, essayOpen])

	const saveNow = useCallback(async () => {
		if (
			!isEditing ||
			!item?.essayId ||
			!item?.versionId ||
			!editorRef.current?.getSnapshot
		)
			return
		const snapshot = editorRef.current.getSnapshot()
		if (!snapshot) return
		const payload = {
			title: titleDraft,
			content_json: snapshot.contentJson,
			content_text: snapshot.contentHtml,
			word_count: snapshot.wordCount,
		}
		const signature = JSON.stringify({
			title: titleDraft,
			contentJson: payload.content_json,
			contentText: payload.content_text,
		})
		if (signature === lastSavedRef.current) return
		setSaving(true)
		try {
			await patchEssayVersion(item.essayId, item.versionId, payload)
			lastSavedRef.current = signature
			onChanged?.()
		} catch (e) {
			console.error(e)
		} finally {
			setSaving(false)
		}
	}, [isEditing, item?.essayId, item?.versionId, onChanged, titleDraft])

	useEffect(() => {
		if (!isEditing) return undefined
		const id = window.setInterval(saveNow, 1500)
		return () => {
			window.clearInterval(id)
			saveNow()
		}
	}, [isEditing, saveNow])

	const handleApprove = async () => {
		if (!item?.essayId || !item?.versionId || actionLoading) return
		setActionLoading(true)
		try {
			await saveNow()
			await patchEssayVersion(item.essayId, item.versionId, {
				status: 'approved',
			})
			await patchVersionChatReadForCounselor(item.essayId, item.versionId)
			onResolvePending?.()
			onClose()
		} catch (e) {
			console.error(e)
		} finally {
			setActionLoading(false)
		}
	}

	const handleSendFeedback = useCallback(
		async text => {
			const trimmed = String(text || '').trim()
			if (
				!trimmed ||
				!item?.essayId ||
				!item?.versionId ||
				actionLoading ||
				feedbackSending
			) {
				return
			}
			setFeedbackSending(true)
			try {
				await postVersionChatMessage(item.essayId, item.versionId, {
					message_text: trimmed.slice(0, 2000),
				})
				await patchVersionChatReadForCounselor(item.essayId, item.versionId)
				await loadFeedback(version, { silent: true })
				onChanged?.()
			} catch (e) {
				console.error(e)
			} finally {
				setFeedbackSending(false)
			}
		},
		[
			actionLoading,
			feedbackSending,
			item?.essayId,
			item?.versionId,
			loadFeedback,
			onChanged,
			version,
		],
	)

	const handleAnchorClick = anchor => {
		const thread = feedbackItems.find(
			feedback =>
				feedback?.resolvedAnchor?.start === anchor.from &&
				feedback?.resolvedAnchor?.end === anchor.to,
		)
		if (!thread) return
		setActiveFeedbackId(thread.id)
		editorRef.current?.setActiveAnchor?.({
			from: anchor.from,
			to: anchor.to,
		})
	}

	if (!item) return null

	const contentJson = parseJson(version?.content_json)
	const wordCount = Number(version?.word_count) || 0
	const wordLimit = Number(item?.wordLimit) > 0 ? Number(item.wordLimit) : 600

	const headerActions = (
		<>
			<Button
				size="sm"
				variant="secondary"
				onClick={() => setEssayOpen(v => !v)}
				leftIcon={<DockIcon />}
				disabled={actionLoading}
			>
				{essayOpen ? 'Hide Essay' : 'Open Essay'}
			</Button>
			{essayOpen ? (
				<Button
					size="sm"
					variant="secondary"
					onClick={() => setIsEditing(v => !v)}
					leftIcon={<PenIcon className={s.penIcon} />}
					disabled={actionLoading}
				>
					{isEditing ? 'Done' : 'Edit'}
				</Button>
			) : null}
			<Button
				size="sm"
				onClick={handleApprove}
				leftIcon={<CheckIcon />}
				disabled={actionLoading}
			>
				{actionLoading ? 'Saving...' : 'Approve'}
			</Button>
		</>
	)

	return (
		<PriorityDrawerShell
			item={item}
			items={items}
			index={index}
			onClose={onClose}
			onNavigate={onNavigate}
			wide={essayOpen}
			feedbackOnly={!essayOpen}
			headerActions={headerActions}
			navigationType="simple"
			bodyClassName={[s.bodyGrid, essayOpen ? s.split : '']
				.filter(Boolean)
				.join(' ')}
		>
			{essayOpen ? (
				<EssayContent
					ref={editorRef}
					loading={loading}
					title={titleDraft || 'Essay'}
					isEditing={isEditing}
					onTitleChange={setTitleDraft}
					versionNumber={item.versionNumber || '1'}
					universityName={item.universityName}
					contentLabel="Student Essay"
					wordCount={wordCount}
					wordLimit={wordLimit}
					versionId={item.versionId}
					contentJson={contentJson}
					contentText={version?.content_text || ''}
					onAnchorClick={handleAnchorClick}
					selectionDisabled={isEditing}
					saving={saving}
					onBackgroundClick={() => setEssayOpen(false)}
				/>
			) : null}
			<FeedbackPanel
				essayId={item.essayId}
				versionId={item.versionId}
				open
				expanded={!essayOpen}
				showHead={essayOpen}
				items={feedbackItems}
				loading={feedbackLoading}
				activeFeedbackId={activeFeedbackId}
				onSend={handleSendFeedback}
				sendDisabled={actionLoading || feedbackSending}
			/>
		</PriorityDrawerShell>
	)
}

export default FeedbackReviewDrawer
